import type {RouteRecordRaw} from 'vue-router'

// Build-time enumeration of markdown pages under db/content/www/pages/<category>/<slug>.md
// (one category level deep, matching the backend WwwMarkdownPage loader). Adding a markdown file
// — in dev via Vite autoBuild, in prod via deploy — creates its route automatically. We only read
// the file KEYS to derive category + slug; page content is fetched from the API at runtime.
// Relative path to db/content/www/pages/<category>/<slug>.md (Vite's glob needs a literal path
// starting with ./ or ../, not an alias). We read only the KEYS to derive category + slug.
const pageModules = import.meta.glob('../../../db/content/www/pages/*/*.md', {query: '?raw'})

export interface MarkdownPageRef {
    category: string
    slug: string
}

function parsePageRefs(): MarkdownPageRef[] {
    return Object.keys(pageModules).map((path) => {
        const segments = path.replace(/\?.*$/, '').split('/').filter(Boolean)
        const slug = segments[segments.length - 1].replace(/\.md$/, '')
        const category = segments[segments.length - 2]
        return {category, slug}
    })
}

export const markdownPageRefs: MarkdownPageRef[] = parsePageRefs()

export const markdownPageCategories: string[] = [...new Set(markdownPageRefs.map(ref => ref.category))].sort()

export const markdownPageRoutes: RouteRecordRaw[] = [
    // Internal index listing every markdown page by category. Intentionally not linked anywhere on
    // the site and excluded from the sitemap — a quick check of what pages are loaded.
    {
        path: '/pages',
        component: () => import('@/components/www/markdown-pages/index/Page.vue'),
    },
    // Per-category index (e.g. /legal → "Legal Pages"). Reached from page breadcrumbs; not in sitemap.
    ...markdownPageCategories.map((category): RouteRecordRaw => ({
        path: `/${category}`,
        component: () => import('@/components/www/markdown-pages/category/Page.vue'),
        props: {category},
    })),
    // Individual pages (e.g. /legal/terms-of-service).
    ...markdownPageRefs.map(({category, slug}): RouteRecordRaw => ({
        path: `/${category}/${slug}`,
        component: () => import('@/components/www/markdown-pages/show/Page.vue'),
        props: {category, slug},
    })),
]
